import type { ModerationScan } from './sessions/moderation';
import type { SessionEscalation } from './agents/escalations';
import { UUID } from 'crypto';

export enum LicenseType {
    TRIAL = 'trial',
    STARTER = 'starter',
    STANDARD = 'standard',
    PRO = 'pro',
    BUSINESS = 'business',
    ACTIVATE = 'activate',
    AIMDOC = 'aimdoc',
    UNKNOWN = 'unknown',
}

export interface DocumentationInterface {
    id: string;
    name: string;
    description: string;
    status: string;
    doc_schema?: Array<FormField>;
    doc_form: any;
    owner: OwnerInterface;
    consumers: Array<OwnerInterface>;
  }

export interface DocumentFileInterface {
  id: string;
  name: string;
  description: string;
  type: string;
  created_at: string;
  updated_at: string;
}

export interface FormField {
  order: number;
  name: string;
  description: string;
  type: string;
  required: boolean;
  enum?: Array<string>;
  value?: any;
  schema?: Array<FormField>;
  path?: string[];
  guid?: string;
}

export interface NewFormField {
  order?: number;
  name: string;
  description: string;
  type: string;
  required: boolean;
  enum?: Array<string>;
  path?: string[];
}

/**
 * A knowledge-base question linked to a transcript message: either the
 * extraction run flagged this AI reply as a failure to answer (status
 * unanswered / for_review) or a reviewer has since written the answer
 * (status answered). Corrected in place from the context drawer.
 */
export interface LinkedQuestion {
  id: string;
  question: string;
  answer?: string | null;
  status?: string | null;
  extraction_reason?: string | null;
  answered_at?: string | null;
  answered_by?: { id: string; first_name?: string | null; last_name?: string | null; email?: string | null } | null;
}

export interface MessageType {
  id?: string;
  content: string;
  type?: string;
  role: string;
  created_at: any;
  metadata?: any;
  fetchDocument?: any;
  docId?: string;
  human_agent?: any;
  agent_name?: string;
  message_metadata?: any;
  question?: LinkedQuestion | null;
  // Session moderation scan of this reply's exchange (dashboard transcript only).
  moderation_scan?: ModerationScan | null;
}

export interface MessageProps {
  content: string;
  role: string;
  metadata?: any;
  fetchDocument?: any;
  created_at?: any;
  docId?: string;
  leadEmail?: any;
  config?: any;
  scrollToBottom?: any;
  sidebar?: boolean;
  gettingResponse?: boolean;
  gettingFullResponse?: boolean;
  human_agent?: any;
  isVisitorTyping?: boolean;
  conversationId?: string;
  message_metadata?: any;
  messageId?: string;
  agentName?: string;
  // Visitor-side messages come from an external AI agent (agent-gateway
  // conversation), not a human — render them distinctly.
  visitorIsAiAgent?: boolean;
  // Dashboard transcript only: the knowledge-base question linked to this
  // reply, and how to reflect a correction back into the transcript.
  question?: LinkedQuestion | null;
  onQuestionChange?: (question: LinkedQuestion) => void;
  // The visitor message this reply answered, to prefill a fresh correction.
  visitorQuestion?: string | null;
    /** Session moderation scan of this reply's exchange (dashboard only). */
    moderationScan?: ModerationScan | null;
  // Dashboard transcript only: the session's escalation attempts, so the
  // escalate tool card can name the rep the chat was routed to.
  escalations?: SessionEscalation[];
}

export interface ConversationProps {
  docAgent: any;
  docId: string | undefined;
}

export interface ExistingChatProps {
  docId: string | undefined;
  docForm: any;
  fetchDocument: any;
  chatId: string | null;
  createType: string | null;
  formFields: Array<FormField>;
  isCollapsed: boolean;
  setIsCollapsed: any;
}

export interface AssistantRequest {
  message: MessageType;
  conversation_id?: string;
  doc_id?: string;
  conversation_type: string;
  context_docs?: Array<string>;
  user_data?: any;
}

export interface AssistantResponse {
  assistant_response: Array<MessageType>;
  document: any;
  conversation: any;
}

export interface Field {
  name: string;
  label: string;
  type: string;
  value?: string;
  agent_instructions: string;
}

export interface DocFormProps {
  docForm: {
      fields: Field[];
  };
  docName: string;
  docDescription: string;
  docId?: string;
  status: string;
  setDocForm: any
  handleSubmit: any;
  owner: any;
  setOwner: any;
  setOwnerNeedsUpdate: any;
  consumers: any;
  addConsumer: any;
  removeConsumer: any;
  setStatus: any;
  setNeedsUpdate: any;
  setDocName: any;
  setDocDescription: any;
  loading: boolean;
}

export interface TeamType {
  id: number;
  name: string;
  description: string;
  users: Array<any>;
  created_by?: any;
}

export interface UserInterface {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  is_active: boolean;
  walkthrough_complete?: boolean;
}

export interface OwnerInterface {
  id: number;
  email?: string;
  name: string;
  type: string;
}

export type ComponentMapping = {
  [key: string]: React.ComponentType<any>;
};

export interface ConversationInterface {
  id: number,
  type: string,
  messages: Array<MessageType>,
  document_id?: number,
}

export interface FilterOption {
  value: string;
  label: string;
  checked: boolean;
}

export interface FilterInterface {
  id: string;
  name: string;
  options: FilterOption[];
}

export type OnFilterChange = (filters: FilterInterface[]) => void;

export interface AgentConfig {
  font: string;
  form: FormField[];
  color: string;
  scheme: string;
  endColor: string;
  // Scoring categories configured in Qualification tab
  scoringCategories?: ScoringCategory[];
  hideAimdoc: boolean;
  mixedColor: string;
  darkerColor: string;
  lighterColor: string;
  popupMessage: string;
  enableCopilot: boolean;
  chatbotEnabled: boolean;
  qualifyVisitor: boolean;
  welcomeMessage: string;
  autoOpenEnabled: boolean;
  autoOpenSeconds: number;
  enableFollowUps: boolean;
  agentMessageIcon: string;
  searchBarEnabled: boolean;
  openOncePerSession: boolean;
  enablePageAwareness: boolean;
  popupMessageEnabled: boolean;
  agentMessageIconType: string;
  followUpInstructions: string;
  searchBarPlaceholder: string;
  enableVisitorLocation: boolean;
  contextualPopupEnabled: boolean;
  contextualStartersEnabled: boolean;
  contextualExclusionRules?: { id: string; condition: 'equals' | 'contains'; url: string }[];
  onboardingUrl?: string;
  onboardingAllowlistUrls?: string[];
  onlyShowForAdmin?: boolean;
}

export interface ScoringCategory {
  id: string;
  name: string;
  instructions: string;
}

interface Agent {
  goal: string;
  starters: any[];
  parent_agent_id: string | null;
  type: string;
  config: AgentConfig;
  id: string;
  logo_key: string | null;
  created_at: string;
  organization_id: string;
  message_icon_key: string | null;
  updated_at: string;
  document_id: string | null;
  fields: any[];
  status: string;
  name: string;
  tools: any | null;
  description: string;
  links: any[];
  persona: string;
}

export interface Question {
  id: string;
  status: 'answered' | 'unanswered' | 'for_review';
  agent_id: string | null;
  created_at: string;
  updated_at: string;
  question: string;
  answer: string | null;
  extraction_reason: string | null;
  extraction_confidence: number | null;
  organization_id: string;
  first_conversation_id: string | null;
  agent: Agent | null;
}